/* eslint-disable react-hooks/rules-of-hooks */
import {
  FilteredModifierValues,
  FilteredValues,
  GroupPrices,
  Modifier,
  PriceDeviation,
  PriceDeviationResponse,
  StoreProduct,
  Variant,
} from '@/types/menus.types';
import { StoreAndGroupType } from '@/context/context.types';
import _ from 'lodash';

const normalizeMenuData = (filteredProducts: StoreProduct[]): StoreProduct[] => {
  const allProducts = filteredProducts;

  return allProducts;
};

const normalizeMenuModifiersData = (filteredProducts: Modifier[]): Modifier[] => {
  const allProducts = filteredProducts;

  return allProducts;
};

export const getDinamicHeaders = (storesAndGroups: StoreAndGroupType) => {
  let allHeaders = storesAndGroups.storeGroups?.flatMap((group) => group.storeGroup) || [];
  if (allHeaders.length === 0) {
    allHeaders = storesAndGroups.stores?.flatMap((group) => group.storeGroup || []) || [];
  }
  const uniq = _.uniqBy(allHeaders, (header) => header);
  const sorted = _.sortBy(uniq, (header) => header);

  return sorted;
};

const filterProducts = (product: StoreProduct, filteredValues: FilteredValues): boolean => {
  const { productName = '', option1 = '', option2 = '', subCategory = '' } = filteredValues;
  const description = product.productDescription.toLowerCase();
  const name = product.productName.toLowerCase();
  const productCode = product.productCode.toLowerCase();

  let optionOk = true;
  if (option1 && option2) {
    optionOk = description.includes(option1.toLowerCase()) && description.includes(option2.toLowerCase());
  } else if (option1) {
    optionOk = description.includes(option1.toLowerCase());
  } else if (option2) {
    optionOk = description.includes(option2.toLowerCase());
  }
  const nameOK = productName ? name.includes(productName.toLowerCase()) : true;
  const productCodeOK = subCategory ? productCode.includes(subCategory.toLowerCase()) : true;

  return optionOk && nameOK && productCodeOK && product.storeGroups.length > 0;
};

const filterModifiers = (product: Modifier, filteredValues: FilteredModifierValues): boolean => {
  const { modifierName = '', option1 = '', option2 = '', subCategory = '' } = filteredValues;
  const description = product.modifierDescription.toLowerCase();
  const name = product.modifierName.toLowerCase();

  let optionOk = true;
  if (option1 && option2) {
    optionOk = description.includes(option1.toLowerCase()) && description.includes(option2.toLowerCase());
  } else if (option1) {
    optionOk = description.includes(option1.toLowerCase());
  } else {
    optionOk = description.includes(option2.toLowerCase());
  }
  const nameOK = name.includes(modifierName.toLowerCase()) && product.storeGroups.length > 0;

  return optionOk && nameOK;
};

export const getStoreFilteredProducts = (prods: StoreProduct[], filteredValues: FilteredValues): StoreProduct[] => {
  const { price = '' } = filteredValues;
  let filteredProducts: StoreProduct[] = prods;
  if (price) {
    filteredProducts = prods
      .map((product) => ({
        ...product,
        storeGroups: product.storeGroups.filter((storeGroup) => {
          let priceValue: number | null = null;
          if (price.startsWith('$')) {
            priceValue = Number(price.replace('$', ''));
          }
          if (priceValue !== null) {
            const { chosenPriceRange } = storeGroup;

            if (typeof chosenPriceRange === 'number') {
              return chosenPriceRange === priceValue;
            }

            if (typeof chosenPriceRange === 'string') {
              const [min, max] = chosenPriceRange.split('-').map(Number);
              if (max === undefined) {
                const chosenPriceValue = Number(chosenPriceRange);
                return chosenPriceValue === priceValue;
              }
              return priceValue >= min && priceValue <= max;
            }
          }
          return true;
        }),
      }))
      .filter((product) => filterProducts(product, filteredValues));
  } else {
    filteredProducts = prods.filter((product) => filterProducts(product, filteredValues));
  }

  return normalizeMenuData(filteredProducts);
};

export const getStoreFilteredModifiers = (prods: Modifier[], filteredValues: FilteredModifierValues): Modifier[] => {
  const { price = '' } = filteredValues;
  let filteredProducts: Modifier[] = prods;
  if (price) {
    filteredProducts = prods
      .map((product) => ({
        ...product,
        storeGroups: product.storeGroups.filter((storeGroup) => {
          let priceValue: number | null = null;
          if (price.startsWith('$')) {
            priceValue = Number(price.replace('$', ''));
          }
          if (priceValue !== null) {
            const { chosenPriceRange } = storeGroup;

            if (typeof chosenPriceRange === 'number') {
              return chosenPriceRange === priceValue;
            }

            if (typeof chosenPriceRange === 'string') {
              const [min, max] = chosenPriceRange.split('-').map(Number);
              if (max === undefined) {
                const chosenPriceValue = Number(chosenPriceRange);
                return chosenPriceValue === priceValue;
              }
              return priceValue >= min && priceValue <= max;
            }
          }
          return true;
        }),
      }))
      .filter((product) => filterModifiers(product, filteredValues));
  } else {
    filteredProducts = prods.filter((product) => filterModifiers(product, filteredValues));
  }

  return normalizeMenuModifiersData(filteredProducts);
};

export function getStores(storesAndGroups: StoreAndGroupType) {
  if (storesAndGroups.origin === 'tab-store') {
    return storesAndGroups.stores?.map((store) => store.storeNo) || [];
  }
  return storesAndGroups.storeGroups?.flatMap((group) => group.stores) || [];
}

export function getStoreGroups(storesAndGroups: StoreAndGroupType) {
  if (storesAndGroups.origin === 'tab-store') {
    return [];
  }
  return storesAndGroups.storeGroups?.filter((group) => group.stores.length === group.storeCount) || [];
}

export function isPriceInvalid(inputPrice: string, lowestValidPrice: string, highestValidPrice: string) {
  const price = Number(inputPrice.replace('$', ''));
  const lowerPrice = Number(lowestValidPrice);
  const highPrice = Number(highestValidPrice);

  const invalid = price < lowerPrice || price > highPrice;

  return invalid;
}

export function combineStoreGroupPrices(storeProducts: StoreProduct[], storesAndGroups: StoreAndGroupType) {
  const storeGroupList = storeProducts?.flatMap((product) => product.storeGroups);

  const count = storesAndGroups?.countByStoreGroup;

  const result = storeGroupList.reduce((acc: GroupPrices[], curr) => {
    const existingStoreGroup = acc.find((group) => group.storeGroupName === curr?.storeGroupName);

    //values used when FZ user selects stores via StoreGroup tab in menu page
    const totalStoreCount = count?.find((obj) => obj.storeGroup === curr?.storeGroupName);
    const selectStoreGroupCount = storesAndGroups.storeGroups?.filter(
      (group) => group.storeGroup === curr?.storeGroupName,
    );

    //values used when FZ user selects stores via All Stores tab in menu page
    const selectAllStoreCount = storesAndGroups.stores?.filter((group) => group.storeGroup === curr?.storeGroupName);
    const selectedAllStores = selectAllStoreCount?.filter((store) => store.storeGroup === curr?.storeGroupName);
    const selectedAllStoreCount = selectedAllStores?.length;

    if (existingStoreGroup) {
      existingStoreGroup.prices.push(curr?.chosenPriceRange);
    } else if (selectStoreGroupCount) {
      acc.push({
        storeGroupName: curr?.storeGroupName,
        prices: [curr?.chosenPriceRange],
        selectedStoreCount: selectStoreGroupCount[0]?.stores?.length,
        totalStoreCount: selectStoreGroupCount[0]?.storeCount,
        stores: curr.stores,
      });
    } else if (selectAllStoreCount) {
      acc.push({
        storeGroupName: curr?.storeGroupName,
        prices: [curr?.chosenPriceRange],
        selectedStoreCount: selectedAllStoreCount || 0,
        totalStoreCount: totalStoreCount?.count || 0,
        stores: curr.stores,
      });
    }
    return acc;
  }, []);
  return result;
}
export function splitArray(toSplit: any[] = []) {
  const middleIndex = Math.ceil(toSplit.length / 2);
  const firstHalf = toSplit.slice(0, middleIndex);
  const secondHalf = toSplit.slice(middleIndex);
  return [firstHalf, secondHalf];
}

export const checkIfHasVariants = (variants: Variant[]) =>
  variants && variants?.some((variant) => variant.excludedStores && variant.excludedStores.length > 0);

export const getUniqStores = (variants: Variant[]) => {
  const variantStores = variants.flatMap((sp) => sp.variantStores);
  const uniqueStores = _.uniqBy(variantStores, (store) => store);

  return uniqueStores || [];
};

export function getRenderablePriceDeviationsData(data: PriceDeviationResponse[]) {
  const storesSuperSet: Set<string> = new Set();
  data.forEach((product: PriceDeviationResponse) => {
    product.priceDeviations.forEach((deviation: PriceDeviation) => storesSuperSet.add(deviation.storeNumber));
  });
  const sortedStores: string[] = Array.from(storesSuperSet).sort();
  const renderableData = data.map((product: PriceDeviationResponse) => {
    const newPriceDeviations: PriceDeviation[] = [];
    sortedStores.forEach((store: string) => {
      const deviation = product.priceDeviations.find((item: PriceDeviation) => item.storeNumber === store);
      if (deviation) {
        newPriceDeviations.push(deviation);
      } else {
        newPriceDeviations.push({ storeNumber: store, price: '' });
      }
    });
    return { ...product, priceDeviations: newPriceDeviations };
  });
  return { sortedStores, renderableData };
}
